import React, { lazy, Suspense, useEffect } from "react";
import { BRAND, IS_GREENONE } from "./config/brand";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import SunLoader from "./components/SunLoader";
import "./App.css";

// iter327 — suppress known-safe hydration warning caused by the platform's
// visual-editor wrapping `.map()` expressions in <span data-ve-dynamic
// style="display:contents">. These wrappers are technically illegal as
// children of <colgroup>/<tr>/<tbody>/<select>/<option>, but they have
// display:contents so the browser still renders correctly. We filter only
// this specific known-noise pattern; all other validateDOMNesting warnings
// remain visible.
if (typeof window !== "undefined" && !window.__a2_console_patched) {
  window.__a2_console_patched = true;
  const _origError = console.error.bind(console);
  console.error = (...args) => {
    try {
      const msg = String(args[0] || "");
      if (msg.includes("cannot be a child of") || msg.includes("This will cause a hydration error")) {
        // Check if any arg or the rendered tree mentions the platform's
        // visual-editor span (data-ve-dynamic / x-excluded display:contents).
        const joined = args.map((a) => {
          if (a && typeof a === "object") {
            try { return JSON.stringify(a); } catch { return String(a); }
          }
          return String(a);
        }).join(" ");
        if (joined.includes("data-ve-dynamic") || joined.includes("x-excluded") || joined.includes("display: contents") ||
            // Argument-substitution format: "%s ... %s" with span + container args
            (args.includes("span") && (args.includes("colgroup") || args.includes("tbody") || args.includes("tr") || args.includes("select") || args.includes("option") || args.includes("col") || args.includes("th") || args.includes("td")))) {
          return; // suppress
        }
      }
    } catch { /* noop */ }
    return _origError(...args);
  };
}

// Critical path - loaded eagerly (minimal for initial render)
import AuthCallback from "./pages/AuthCallback";
import AdminLayout from "./pages/admin/AdminLayout";
import GlobalSmsDialog from "./components/GlobalSmsDialog";
import GlobalQuickCapture from "./components/GlobalQuickCapture";
import GlobalAutoNew from "./components/GlobalAutoNew";
import StatusChangePromptDialog from "./components/StatusChangePromptDialog";
import ConfigWizard from "./pages/admin/ConfigWizard";

// Lazy loaded - split into separate chunks
const Login = lazy(() => import("./pages/Login"));
// iter413j — iPhone-first Mobile SPA
const MobileLogin = lazy(() => import("./pages/mobile/MobileLogin"));
const MobileLayout = lazy(() => import("./pages/mobile/MobileLayout"));
const MobileDashboard = lazy(() => import("./pages/mobile/MobileDashboard"));
const MobileCustomers = lazy(() => import("./pages/mobile/MobileCustomers"));
const MobileCustomerDetail = lazy(() => import("./pages/mobile/MobileCustomerDetail"));
const MobileTasks = lazy(() => import("./pages/mobile/MobileTasks"));
const MobileQuotes = lazy(() => import("./pages/mobile/MobileQuotes").then(m => ({ default: m.MobileQuotes })));
const MobileQuoteDetail = lazy(() => import("./pages/mobile/MobileQuotes").then(m => ({ default: m.MobileQuoteDetail })));
const MobileClaims = lazy(() => import("./pages/mobile/MobileClaims").then(m => ({ default: m.MobileClaims })));
const MobileClaimDetail = lazy(() => import("./pages/mobile/MobileClaims").then(m => ({ default: m.MobileClaimDetail })));
const MobileServiceOrders = lazy(() => import("./pages/mobile/MobileService").then(m => ({ default: m.MobileServiceOrders })));
const MobileServiceDetail = lazy(() => import("./pages/mobile/MobileService").then(m => ({ default: m.MobileServiceDetail })));
const MobileEshopOrders = lazy(() => import("./pages/mobile/MobileEshopOrders").then(m => ({ default: m.MobileEshopOrders })));
const MobileEshopOrderDetail = lazy(() => import("./pages/mobile/MobileEshopOrders").then(m => ({ default: m.MobileEshopOrderDetail })));
const MobileMore = lazy(() => import("./pages/mobile/MobileMore"));
const FormWizard = lazy(() => import("./pages/FormWizard"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));

// Lazy loaded - split into separate chunks
const CustomerPortal = lazy(() => import("./pages/CustomerPortal"));
const PublicQuote = lazy(() => import("./pages/PublicQuote"));
// iter375 — Nabídky 2 (paralelní moderní UX)
const Quote2Public = lazy(() => import("./pages/nabidky2/Quote2Public"));
const Nabidky2List = lazy(() => import("./pages/nabidky2/Nabidky2List"));
// iter395 — E-shop (public storefront)
const EshopCatalog = lazy(() => import("./pages/eshop/EshopStorefront").then(m => ({ default: m.EshopCatalog })));
const EshopProductDetail = lazy(() => import("./pages/eshop/EshopStorefront").then(m => ({ default: m.EshopProductDetail })));
const EshopCheckout = lazy(() => import("./pages/eshop/EshopStorefront").then(m => ({ default: m.EshopCheckout })));
const EshopOrderConfirmation = lazy(() => import("./pages/eshop/EshopStorefront").then(m => ({ default: m.EshopOrderConfirmation })));
const B2BLogin = lazy(() => import("./pages/eshop/B2BPortal").then(m => ({ default: m.B2BLogin })));
const B2BDashboard = lazy(() => import("./pages/eshop/B2BPortal").then(m => ({ default: m.B2BDashboard })));
const B2BPricelist = lazy(() => import("./pages/eshop/B2BPortal").then(m => ({ default: m.B2BPricelist })));
const B2BCart = lazy(() => import("./pages/eshop/B2BPortal").then(m => ({ default: m.B2BCart })));
const B2BOrders = lazy(() => import("./pages/eshop/B2BPortal").then(m => ({ default: m.B2BOrders })));
const Nabidky2Studio = lazy(() => import("./pages/nabidky2/Nabidky2Studio"));
const Nabidky2Analytics = lazy(() => import("./pages/nabidky2/Nabidky2Analytics"));
const QuoteDefaultsEditor = lazy(() => import("./pages/admin/QuoteDefaultsEditor"));
const HtmlTemplatesAdmin = lazy(() => import("./pages/admin/html_templates/HtmlTemplatesAdmin").then(m => ({ default: m.HtmlTemplatesList })));
const HtmlTemplateEditor = lazy(() => import("./pages/admin/html_templates/HtmlTemplatesAdmin").then(m => ({ default: m.HtmlTemplateEditor })));
const PublicContractSign = lazy(() => import("./pages/PublicContractSign"));
const CSATFeedback = lazy(() => import("./pages/CSATFeedback"));
const Submissions = lazy(() => import("./pages/admin/Submissions"));
const SubmissionDetail = lazy(() => import("./pages/admin/SubmissionDetail"));
const Pipeline = lazy(() => import("./pages/admin/Pipeline"));
// iter373 — Pipeline route alias: redirect to merged Submissions
const PipelineRedirect = lazy(() => import("./pages/admin/PipelineRedirect"));
const AiBudget = lazy(() => import("./pages/admin/AiBudget"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const AiAgent = lazy(() => import("./pages/admin/AiAgent"));
const AiAnalytics = lazy(() => import("./pages/admin/AiAnalytics"));
const Revize = lazy(() => import("./pages/admin/Revize"));
const Penb = lazy(() => import("./pages/admin/Penb"));
const Settings = lazy(() => import("./pages/admin/Settings"));
const EmailTemplates = lazy(() => import("./pages/admin/EmailTemplates"));
const ApiKeys = lazy(() => import("./pages/admin/ApiKeys"));
const Users = lazy(() => import("./pages/admin/Users"));
const PdfEditorPage = lazy(() => import("./pages/admin/pdf_editor/PdfEditorPage"));
const Professionals = lazy(() => import("./pages/admin/Professionals"));
const Quotes = lazy(() => import("./pages/admin/Quotes"));
const Invoices = lazy(() => import("./pages/admin/Invoices"));
const Projects = lazy(() => import("./pages/admin/Projects"));
const Tasks = lazy(() => import("./pages/admin/Tasks"));
const Claims = lazy(() => import("./pages/admin/Claims"));
const ServiceOrders = lazy(() => import("./pages/admin/ServiceOrders"));
const Rentals = lazy(() => import("./pages/admin/Rentals"));
const Customers = lazy(() => import("./pages/admin/Customers"));
const SystemDiagnostics = lazy(() => import("./pages/admin/SystemDiagnostics"));
const AdvancedSettings = lazy(() => import("./pages/admin/AdvancedSettings"));
const SystemUpdates = lazy(() => import("./pages/admin/SystemUpdates"));
const MobileApi = lazy(() => import("./pages/admin/MobileApi"));
const PushTemplates = lazy(() => import("./pages/admin/PushTemplates"));
const SystemMessages = lazy(() => import("./pages/admin/SystemMessages"));
const Automation = lazy(() => import("./pages/admin/Automation"));
const Timesheets = lazy(() => import("./pages/admin/Timesheets"));
const ScheduledTasks = lazy(() => import("./pages/admin/ScheduledTasks"));
const Calls = lazy(() => import("./pages/admin/Calls"));  // iter413 — Volání module
const Diagnostika = lazy(() => import("./pages/admin/Diagnostika"));
// iter413c-21 — Data hygiene anomalies (stock_alert, slug_drift, duplicate_sku, ...)
const Diagnostics = lazy(() => import("./pages/admin/Diagnostics"));
const OpsAlerts = lazy(() => import("./pages/admin/OpsAlerts"));
const B2BCompanies = lazy(() => import("./pages/admin/B2BCompanies"));
const EshopAdmin = lazy(() => import("./pages/admin/EshopAdmin"));
const ClientErrors = lazy(() => import("./pages/admin/ClientErrors"));
const AdminDesktop = lazy(() => import("./pages/admin2/AdminDesktop"));
const ComingSoonModule = lazy(() => import("./pages/admin/ComingSoonModule"));
const Trash = lazy(() => import("./pages/admin/Trash"));
const RolesPermissions = lazy(() => import("./pages/admin/RolesPermissions"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const ServerDiagnostics = lazy(() => import("./pages/admin/ServerDiagnostics"));
const DataManagement = lazy(() => import("./pages/admin/DataManagement"));
const ItemsCatalog = lazy(() => import("./pages/admin/ItemsCatalog"));
const Warehouse = lazy(() => import("./pages/admin/Warehouse"));
const Scanner = lazy(() => import("./pages/admin/Scanner"));
const CustomersKanban = lazy(() => import("./pages/admin/CustomersKanban"));
const Purchasing = lazy(() => import("./pages/admin/Purchasing"));
const Planning = lazy(() => import("./pages/admin/Planning"));
const Banking = lazy(() => import("./pages/admin/Banking"));
const Manufacturing = lazy(() => import("./pages/admin/Manufacturing"));
const ProductionOrders = lazy(() => import("./pages/admin/ProductionOrders"));
const DemoData = lazy(() => import("./pages/admin/DemoData"));
const Protocols = lazy(() => import("./pages/admin/Protocols"));
const WhatsAppAgent = lazy(() => import("./pages/admin/WhatsAppAgent"));
// iter413ax — Demo Leads inbox page (hot leads from sandbox)
const DemoLeadsInbox = lazy(() => import("./pages/admin/DemoLeadsInbox"));
const Contracts = lazy(() => import("./pages/admin/Contracts"));
const Subsidies = lazy(() => import("./pages/admin/Subsidies"));
const Templates = lazy(() => import("./pages/admin/Templates"));
const TemplatesHub = lazy(() => import("./pages/admin/templates_hub/TemplatesHub"));
const Manufacturers = lazy(() => import("./pages/admin/Manufacturers"));
const EshopCategoriesAdmin = lazy(() => import("./pages/admin/EshopCategoriesAdmin"));  // iter406
const PhotoLibrary = lazy(() => import("./pages/admin/PhotoLibrary"));
const Calendar = lazy(() => import("./pages/admin/Calendar"));
const CalendarSettings = lazy(() => import("./pages/admin/CalendarSettings"));
const Documents = lazy(() => import("./pages/admin/Documents"));
const Files = lazy(() => import("./pages/admin/Files"));
const Sms = lazy(() => import("./pages/admin/Sms"));
const Emails = lazy(() => import("./pages/admin/Emails"));
const Chat = lazy(() => import("./pages/admin/Chat"));
const Installations = lazy(() => import("./pages/admin/Installations"));
const Activity = lazy(() => import("./pages/admin/Activity"));
const Learn = lazy(() => import("./pages/admin/Learn"));
const Accounting = lazy(() => import("./pages/admin/Accounting"));
const Scenarios = lazy(() => import("./pages/admin/Scenarios"));
const PhoneHistory = lazy(() => import("./pages/admin/PhoneHistory"));
const PublicDiagnostics = lazy(() => import("./pages/PublicDiagnostics"));
const Prezentace = lazy(() => import("./pages/Prezentace"));
const Remeslnici = lazy(() => import("./pages/Remeslnici"));
const ProfessionalSelfProfile = lazy(() => import("./pages/ProfessionalSelfProfile"));
// iter413c — Multi-tenant E-shop admin pages
const EshopRegistry = lazy(() => import("./pages/admin/eshop/EshopAdmin"));
const GreenoneInstances = lazy(() => import("./pages/admin/GreenoneInstances"));
const EshopProducts = lazy(() => import("./pages/admin/eshop/EshopProducts"));
const EshopCategories = lazy(() => import("./pages/admin/eshop/EshopCategories"));
const EshopPages = lazy(() => import("./pages/admin/eshop/EshopPages"));
const EshopOrdersMT = lazy(() => import("./pages/admin/eshop/EshopOrders"));
const EshopCustomers = lazy(() => import("./pages/admin/eshop/EshopCustomers"));
const EshopSettingsMT = lazy(() => import("./pages/admin/eshop/EshopSettings"));
// Multi-tenant storefront
const MultiEshopStorefront = lazy(() => import("./pages/eshop/MultiEshopStorefront"));
const MultiEshopPage = lazy(() => import("./pages/eshop/MultiEshopPage"));
const MultiEshopCheckout = lazy(() => import("./pages/eshop/MultiEshopCheckout"));
const MultiEshopOrderConfirm = lazy(() => import("./pages/eshop/MultiEshopOrderConfirm"));
const MultiEshopProductDetail = lazy(() => import("./pages/eshop/MultiEshopProductDetail"));
// iter413c-13 — Multi-tenant B2B login portal
const MultiEshopB2BLogin = lazy(() => import("./pages/eshop/MultiEshopB2BLogin"));
// iter413ap — Demo signup landing page
const DemoSignup = lazy(() => import("./pages/DemoSignup"));

// iter413c-13 — New admin pages
const LicencesAdmin = lazy(() => import("./pages/admin/LicencesAdmin"));
const MasterDataHealth = lazy(() => import("./pages/admin/MasterDataHealth"));
// iter413c-15 — Anomaly admin (digests + manual trigger)
const AnomalyAdmin = lazy(() => import("./pages/admin/AnomalyAdmin"));

function LazyFallback() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <SunLoader size="md" text="Načítám modul..." />
    </div>
  );
}

// iter413c — Custom domain detection
//
// Hosts that map to the *primary* CRM application. Anything else is treated as
// a custom-domain landing for a multi-tenant e-shop and routes "/" to the
// storefront (which calls `/api/v1/public/eshop/resolve` with the Host header
// to find the right shop).
const CRM_HOSTS = new Set([
  "poptavka.solar-green.cz",
  "crm-stabilized.preview.emergentagent.com",
  "localhost",
  "127.0.0.1",
]);

// GreenONE V3 — presentation-first hosts (green-one.cz) are resolved via the
// centralized `IS_GREENONE` flag from config/brand. On those hosts "/" renders
// the marketing presentation and the inquiry form moves to "/poptavka".
function isCustomEshopHost() {
  if (typeof window === "undefined") return false;
  const h = (window.location.hostname || "").toLowerCase();
  if (!h) return false;
  if (CRM_HOSTS.has(h)) return false;
  if (IS_GREENONE) return false;
  // Treat *.solar-green.cz subdomains as primary unless explicitly e-shop
  if (h.endsWith(".solar-green.cz") && h !== "poptavka.solar-green.cz") return false;
  // Treat *.preview.emergentagent.com as primary preview
  if (h.endsWith(".preview.emergentagent.com")) return false;
  return true;
}

function HostAwareRoot() {
  // GreenONE V3 — presentation-first domains land on the marketing page.
  if (IS_GREENONE) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <Prezentace />
      </Suspense>
    );
  }
  // For custom domains we render the multi-tenant storefront which itself
  // resolves the slug via the Host header. Primary CRM domains keep the
  // legacy FormWizard landing.
  if (isCustomEshopHost()) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopStorefront />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LazyFallback />}>
      <FormWizard />
    </Suspense>
  );
}

// iter413c — Multi-tenant URL parser
//
// React Router v7 doesn't support mid-segment params (`/eshop_:slug` is
// invalid because `:` must start a segment), so we route URLs like
// `/eshop_<slug>`, `/eshop_<slug>/checkout`,
// `/eshop_<slug>/order/<order_token>` and `/eshop_<slug>/p/<pageSlug>`
// manually here. Children receive the parsed params as props.
function MultiEshopRouter({ pathname }) {
  const stripped = pathname.replace(/^\/eshop_/, "");
  const slashIdx = stripped.indexOf("/");
  const slug = slashIdx === -1 ? stripped : stripped.slice(0, slashIdx);
  const rest = slashIdx === -1 ? "" : stripped.slice(slashIdx);

  if (rest === "/checkout") {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopCheckout slug={slug} />
      </Suspense>
    );
  }
  const orderMatch = rest.match(/^\/order\/([^/]+)/);
  if (orderMatch) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopOrderConfirm slug={slug} orderToken={orderMatch[1]} />
      </Suspense>
    );
  }
  // iter413c-6 — Product detail page
  const productMatch = rest.match(/^\/product\/(.+)$/);
  if (productMatch) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopProductDetail slug={slug} productId={productMatch[1]} />
      </Suspense>
    );
  }
  // iter413c-13 — B2B login portal for this eshop
  if (rest === "/b2b" || rest === "/b2b/login") {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopB2BLogin slug={slug} />
      </Suspense>
    );
  }
  const pageMatch = rest.match(/^\/p\/([^/]+)/);
  if (pageMatch) {
    return (
      <Suspense fallback={<LazyFallback />}>
        <MultiEshopPage slug={slug} pageSlug={pageMatch[1]} />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LazyFallback />}>
      <MultiEshopStorefront slug={slug} />
    </Suspense>
  );
}

function AppRouter() {
  const location = useLocation();
  // GreenONE V3 — host-aware browser tab title.
  useEffect(() => {
    if (BRAND.tabTitle) document.title = BRAND.tabTitle;
  }, []);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // iter413c — Multi-tenant storefront uses /eshop_<slug> URL.
  // React Router v7 doesn't support mid-segment params (`/eshop_:slug` is
  // invalid because the `:` must start a segment), so we route manually.
  if (location.pathname.startsWith("/eshop_")) {
    return <MultiEshopRouter pathname={location.pathname} />;
  }
  return (
    <Routes>
      <Route path="/" element={<HostAwareRoot />} />
      {/* GreenONE V3 — inquiry form moved off "/" to "/poptavka" */}
      <Route path="/poptavka" element={<Suspense fallback={<LazyFallback />}><FormWizard /></Suspense>} />
      <Route path="/portal" element={<Suspense fallback={<LazyFallback />}><CustomerPortal /></Suspense>} />
      <Route path="/quote/public/:token" element={<Suspense fallback={<LazyFallback />}><PublicQuote /></Suspense>} />
      <Route path="/q2/:token" element={<Suspense fallback={<LazyFallback />}><Quote2Public /></Suspense>} />
      <Route path="/contract/sign/:token" element={<Suspense fallback={<LazyFallback />}><PublicContractSign /></Suspense>} />
      <Route path="/csat/:token" element={<Suspense fallback={<LazyFallback />}><CSATFeedback /></Suspense>} />
      <Route path="/diagnostika" element={<Suspense fallback={<LazyFallback />}><PublicDiagnostics /></Suspense>} />
      <Route path="/prezentace" element={<Suspense fallback={<LazyFallback />}><Prezentace /></Suspense>} />
      <Route path="/remeslnici" element={<Suspense fallback={<LazyFallback />}><Remeslnici /></Suspense>} />
      <Route path="/remeslnici/profile/:token" element={<Suspense fallback={<LazyFallback />}><ProfessionalSelfProfile /></Suspense>} />
      {/* iter413ap — Demo investor landing + signup */}
      <Route path="/demo" element={<Suspense fallback={<LazyFallback />}><DemoSignup /></Suspense>} />
      {/* Demo users land on the regular /admin/* routes — middleware + sidebar
          restrict what they see. This alias just lets the signup flow redirect
          here without confusion. */}
      <Route path="/demo/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
      {/* iter395 — E-shop (public) */}
      <Route path="/eshop" element={<Suspense fallback={<LazyFallback />}><EshopCatalog /></Suspense>} />
      {/* iter413c — Multi-tenant storefront /eshop_<slug> is handled
          manually above AppRouter (React Router v7 doesn't support
          mid-segment params). Page detail handled inside the storefront. */}
      <Route path="/eshop/product/:slug" element={<Suspense fallback={<LazyFallback />}><EshopProductDetail /></Suspense>} />
      <Route path="/eshop/cart" element={<Suspense fallback={<LazyFallback />}><EshopCheckout /></Suspense>} />
      <Route path="/eshop/order/:token" element={<Suspense fallback={<LazyFallback />}><EshopOrderConfirmation /></Suspense>} />
      <Route path="/eshop/b2b/login" element={<Suspense fallback={<LazyFallback />}><B2BLogin /></Suspense>} />
      <Route path="/eshop/b2b/pricelist" element={<Suspense fallback={<LazyFallback />}><B2BPricelist /></Suspense>} />
      <Route path="/eshop/b2b/cart" element={<Suspense fallback={<LazyFallback />}><B2BCart /></Suspense>} />
      <Route path="/eshop/b2b/orders" element={<Suspense fallback={<LazyFallback />}><B2BOrders /></Suspense>} />
      <Route path="/eshop/b2b" element={<Suspense fallback={<LazyFallback />}><B2BDashboard /></Suspense>} />
      <Route path="/admin/login" element={<Suspense fallback={<LazyFallback />}><Login /></Suspense>} />
      {/* iter413j — iPhone-first Mobile SPA */}
      <Route path="/m/login" element={<Suspense fallback={<LazyFallback />}><MobileLogin /></Suspense>} />
      <Route path="/m" element={<Suspense fallback={<LazyFallback />}><MobileLayout /></Suspense>}>
        <Route index element={<Suspense fallback={<LazyFallback />}><MobileDashboard /></Suspense>} />
        <Route path="customers" element={<Suspense fallback={<LazyFallback />}><MobileCustomers /></Suspense>} />
        <Route path="customers/:customerId" element={<Suspense fallback={<LazyFallback />}><MobileCustomerDetail /></Suspense>} />
        <Route path="tasks" element={<Suspense fallback={<LazyFallback />}><MobileTasks /></Suspense>} />
        <Route path="quotes" element={<Suspense fallback={<LazyFallback />}><MobileQuotes /></Suspense>} />
        <Route path="quotes/:quoteId" element={<Suspense fallback={<LazyFallback />}><MobileQuoteDetail /></Suspense>} />
        <Route path="claims" element={<Suspense fallback={<LazyFallback />}><MobileClaims /></Suspense>} />
        <Route path="claims/:claimId" element={<Suspense fallback={<LazyFallback />}><MobileClaimDetail /></Suspense>} />
        <Route path="service" element={<Suspense fallback={<LazyFallback />}><MobileServiceOrders /></Suspense>} />
        <Route path="service/:orderId" element={<Suspense fallback={<LazyFallback />}><MobileServiceDetail /></Suspense>} />
        <Route path="eshop-orders" element={<Suspense fallback={<LazyFallback />}><MobileEshopOrders /></Suspense>} />
        <Route path="eshop-orders/:orderId" element={<Suspense fallback={<LazyFallback />}><MobileEshopOrderDetail /></Suspense>} />
        <Route path="more" element={<Suspense fallback={<LazyFallback />}><MobileMore /></Suspense>} />
      </Route>
      <Route path="/admin2" element={<ProtectedRoute><Suspense fallback={<LazyFallback />}><AdminDesktop /></Suspense></ProtectedRoute>} />
      <Route path="/admin/skener" element={<ProtectedRoute><Suspense fallback={<LazyFallback />}><Scanner /></Suspense></ProtectedRoute>} />
      <Route path="/admin/customers-kanban" element={<ProtectedRoute><Suspense fallback={<LazyFallback />}><CustomersKanban /></Suspense></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/admin2" replace />} />
        <Route path="dashboard" element={<Suspense fallback={<LazyFallback />}><Dashboard /></Suspense>} />
        <Route path="submissions" element={<Suspense fallback={<LazyFallback />}><Submissions /></Suspense>} />
        <Route path="submissions/:id" element={<Suspense fallback={<LazyFallback />}><SubmissionDetail /></Suspense>} />
        <Route path="poptavky" element={<Suspense fallback={<LazyFallback />}><Submissions /></Suspense>} />
        <Route path="poptavky/:id" element={<Suspense fallback={<LazyFallback />}><SubmissionDetail /></Suspense>} />
        <Route path="pipeline" element={<Suspense fallback={<LazyFallback />}><PipelineRedirect /></Suspense>} />
        <Route path="ai-budget" element={<Suspense fallback={<LazyFallback />}><AiBudget /></Suspense>} />
        <Route path="categories" element={<Suspense fallback={<LazyFallback />}><Categories /></Suspense>} />
        <Route path="ai-agent" element={<Suspense fallback={<LazyFallback />}><AiAgent /></Suspense>} />
        <Route path="ai-analytics" element={<Suspense fallback={<LazyFallback />}><AiAnalytics /></Suspense>} />
        <Route path="revize" element={<Suspense fallback={<LazyFallback />}><Revize /></Suspense>} />
        <Route path="penb" element={<Suspense fallback={<LazyFallback />}><Penb /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<LazyFallback />}><Settings /></Suspense>} />
        <Route path="email-templates" element={<Suspense fallback={<LazyFallback />}><EmailTemplates /></Suspense>} />
        <Route path="api-keys" element={<Suspense fallback={<LazyFallback />}><ApiKeys /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<LazyFallback />}><Users /></Suspense>} />
        <Route path="pdf-editor" element={<Suspense fallback={<LazyFallback />}><PdfEditorPage /></Suspense>} />
        <Route path="professionals" element={<Suspense fallback={<LazyFallback />}><Professionals /></Suspense>} />
        <Route path="quotes" element={<Suspense fallback={<LazyFallback />}><Quotes /></Suspense>} />
        {/* iter375 — Nabídky 2 admin routes */}
        <Route path="nabidky2" element={<Suspense fallback={<LazyFallback />}><Nabidky2List /></Suspense>} />
        <Route path="nabidky2/studio/:id" element={<Suspense fallback={<LazyFallback />}><Nabidky2Studio /></Suspense>} />
        <Route path="nabidky2/analytics/:id" element={<Suspense fallback={<LazyFallback />}><Nabidky2Analytics /></Suspense>} />
        <Route path="quote-defaults" element={<Suspense fallback={<LazyFallback />}><QuoteDefaultsEditor /></Suspense>} />
        <Route path="html-templates" element={<Suspense fallback={<LazyFallback />}><HtmlTemplatesAdmin /></Suspense>} />
        <Route path="html-templates/:id" element={<Suspense fallback={<LazyFallback />}><HtmlTemplateEditor /></Suspense>} />
        <Route path="invoices" element={<Suspense fallback={<LazyFallback />}><Invoices /></Suspense>} />
        <Route path="projects" element={<Suspense fallback={<LazyFallback />}><Projects /></Suspense>} />
        <Route path="tasks" element={<Suspense fallback={<LazyFallback />}><Tasks /></Suspense>} />
        <Route path="phone-history" element={<Suspense fallback={<LazyFallback />}><PhoneHistory /></Suspense>} />
        <Route path="claims" element={<Suspense fallback={<LazyFallback />}><Claims /></Suspense>} />
        <Route path="service" element={<Suspense fallback={<LazyFallback />}><ServiceOrders /></Suspense>} />
        <Route path="rentals" element={<Suspense fallback={<LazyFallback />}><Rentals /></Suspense>} />
        <Route path="customers" element={<Suspense fallback={<LazyFallback />}><Customers /></Suspense>} />
        <Route path="system" element={<Suspense fallback={<LazyFallback />}><SystemDiagnostics /></Suspense>} />
        <Route path="advanced" element={<Suspense fallback={<LazyFallback />}><AdvancedSettings /></Suspense>} />
        <Route path="system-updates" element={<Suspense fallback={<LazyFallback />}><SystemUpdates /></Suspense>} />
        <Route path="mobile-api" element={<Suspense fallback={<LazyFallback />}><MobileApi /></Suspense>} />
        <Route path="push-templates" element={<Suspense fallback={<LazyFallback />}><PushTemplates /></Suspense>} />
        <Route path="system-messages" element={<Suspense fallback={<LazyFallback />}><SystemMessages /></Suspense>} />
        <Route path="config-wizard" element={<ConfigWizard />} />
        <Route path="automation" element={<Suspense fallback={<LazyFallback />}><Automation /></Suspense>} />
        <Route path="timesheets" element={<Suspense fallback={<LazyFallback />}><Timesheets /></Suspense>} />
        <Route path="scheduled-tasks" element={<Suspense fallback={<LazyFallback />}><ScheduledTasks /></Suspense>} />
        <Route path="calls" element={<Suspense fallback={<LazyFallback />}><Calls /></Suspense>} />
        {/* iter413c — Multi-tenant E-shop admin */}
        <Route path="eshop-sprava" element={<Suspense fallback={<LazyFallback />}><EshopRegistry /></Suspense>} />
        <Route path="eshop-produkty" element={<Suspense fallback={<LazyFallback />}><EshopProducts /></Suspense>} />
        <Route path="eshop-kategorie" element={<Suspense fallback={<LazyFallback />}><EshopCategories /></Suspense>} />
        <Route path="eshop-stranky" element={<Suspense fallback={<LazyFallback />}><EshopPages /></Suspense>} />
        <Route path="eshop-objednavky-mt" element={<Suspense fallback={<LazyFallback />}><EshopOrdersMT /></Suspense>} />
        <Route path="eshop-zakaznici" element={<Suspense fallback={<LazyFallback />}><EshopCustomers /></Suspense>} />
        <Route path="eshop-nastaveni" element={<Suspense fallback={<LazyFallback />}><EshopSettingsMT /></Suspense>} />
        <Route path="greenone-instances" element={<Suspense fallback={<LazyFallback />}><GreenoneInstances /></Suspense>} />
        <Route path="volani" element={<Suspense fallback={<LazyFallback />}><Calls /></Suspense>} />  {/* Czech alias */}
        <Route path="diagnostika" element={<Suspense fallback={<LazyFallback />}><Diagnostika /></Suspense>} />
        {/* iter413c-21 — Data anomaly diagnostics (stock_alert, slug_drift, etc.) */}
        <Route path="diagnostics" element={<Suspense fallback={<LazyFallback />}><Diagnostics /></Suspense>} />
        <Route path="ops-alerts" element={<Suspense fallback={<LazyFallback />}><OpsAlerts /></Suspense>} />
        <Route path="b2b-companies" element={<Suspense fallback={<LazyFallback />}><B2BCompanies /></Suspense>} />
        <Route path="eshop-orders" element={<Suspense fallback={<LazyFallback />}><EshopAdmin /></Suspense>} />
        <Route path="client-errors" element={<Suspense fallback={<LazyFallback />}><ClientErrors /></Suspense>} />
        <Route path="module/:slug" element={<Suspense fallback={<LazyFallback />}><ComingSoonModule /></Suspense>} />
        <Route path="trash" element={<Suspense fallback={<LazyFallback />}><Trash /></Suspense>} />
        <Route path="roles" element={<Suspense fallback={<LazyFallback />}><RolesPermissions /></Suspense>} />
        <Route path="audit-log" element={<Suspense fallback={<LazyFallback />}><AuditLog /></Suspense>} />
        <Route path="server-diagnostics" element={<Suspense fallback={<LazyFallback />}><ServerDiagnostics /></Suspense>} />
        <Route path="data-management" element={<Suspense fallback={<LazyFallback />}><DataManagement /></Suspense>} />
        <Route path="items-catalog" element={<Suspense fallback={<LazyFallback />}><ItemsCatalog /></Suspense>} />
        <Route path="warehouse" element={<Suspense fallback={<LazyFallback />}><Warehouse /></Suspense>} />
        <Route path="purchasing" element={<Suspense fallback={<LazyFallback />}><Purchasing /></Suspense>} />
        <Route path="planning" element={<Suspense fallback={<LazyFallback />}><Planning /></Suspense>} />
        <Route path="banking" element={<Suspense fallback={<LazyFallback />}><Banking /></Suspense>} />
        <Route path="manufacturing" element={<Suspense fallback={<LazyFallback />}><Manufacturing /></Suspense>} />
        <Route path="production-orders" element={<Suspense fallback={<LazyFallback />}><ProductionOrders /></Suspense>} />
        <Route path="demo-data" element={<Suspense fallback={<LazyFallback />}><DemoData /></Suspense>} />
        <Route path="protocols" element={<Suspense fallback={<LazyFallback />}><Protocols /></Suspense>} />
        <Route path="whatsapp" element={<Suspense fallback={<LazyFallback />}><WhatsAppAgent /></Suspense>} />
        <Route path="demo-leads" element={<Suspense fallback={<LazyFallback />}><DemoLeadsInbox /></Suspense>} />
        <Route path="contracts" element={<Suspense fallback={<LazyFallback />}><Contracts /></Suspense>} />
        <Route path="subsidies" element={<Suspense fallback={<LazyFallback />}><Subsidies /></Suspense>} />
        <Route path="templates" element={<Suspense fallback={<LazyFallback />}><Templates /></Suspense>} />
        <Route path="templates-hub" element={<Suspense fallback={<LazyFallback />}><TemplatesHub /></Suspense>} />
        <Route path="manufacturers" element={<Suspense fallback={<LazyFallback />}><Manufacturers /></Suspense>} />
        {/* iter413c-13 — Licences + Master Data Health */}
        <Route path="licences" element={<Suspense fallback={<LazyFallback />}><LicencesAdmin /></Suspense>} />
        <Route path="master-data-health" element={<Suspense fallback={<LazyFallback />}><MasterDataHealth /></Suspense>} />
        <Route path="anomalies" element={<Suspense fallback={<LazyFallback />}><AnomalyAdmin /></Suspense>} />
        <Route path="eshop/categories" element={<Suspense fallback={<LazyFallback />}><EshopCategoriesAdmin /></Suspense>} />
        <Route path="photo-library" element={<Suspense fallback={<LazyFallback />}><PhotoLibrary /></Suspense>} />
        <Route path="calendar" element={<Suspense fallback={<LazyFallback />}><Calendar /></Suspense>} />
        <Route path="calendar/settings" element={<Suspense fallback={<LazyFallback />}><CalendarSettings /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<LazyFallback />}><Documents /></Suspense>} />
        <Route path="files" element={<Suspense fallback={<LazyFallback />}><Files /></Suspense>} />
        <Route path="sms" element={<Suspense fallback={<LazyFallback />}><Sms /></Suspense>} />
        <Route path="emails" element={<Suspense fallback={<LazyFallback />}><Emails /></Suspense>} />
        <Route path="chat" element={<Suspense fallback={<LazyFallback />}><Chat /></Suspense>} />
        <Route path="installations" element={<Suspense fallback={<LazyFallback />}><Installations /></Suspense>} />
        <Route path="activity" element={<Suspense fallback={<LazyFallback />}><Activity /></Suspense>} />
        <Route path="learn" element={<Suspense fallback={<LazyFallback />}><Learn /></Suspense>} />
        <Route path="accounting" element={<Suspense fallback={<LazyFallback />}><Accounting /></Suspense>} />
        <Route path="scenarios" element={<Suspense fallback={<LazyFallback />}><Scenarios /></Suspense>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Global axios interceptor
import axios from "axios";
axios.interceptors.request.use(config => {
  const token = localStorage.getItem("session_token");
  if (token && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  config.withCredentials = false;
  return config;
});

// Response interceptor: normalize paginated `{items, total, page, pages}` responses
// so both `data.items` AND array methods like `data.map(...)` / `for..of` work
// transparently. Prevents the recurring "X.map is not a function" crashes.
const ARRAY_METHODS = ["map", "filter", "forEach", "find", "findIndex", "some", "every",
  "reduce", "reduceRight", "slice", "concat", "includes", "indexOf", "lastIndexOf",
  "flat", "flatMap", "join", "at"];
axios.interceptors.response.use(response => {
  const d = response.data;
  // Only patch plain objects with `items` array and pagination-like shape
  if (
    d && typeof d === "object" && !Array.isArray(d) &&
    Array.isArray(d.items) &&
    ("total" in d || "page" in d || "pages" in d || "limit" in d || "offset" in d)
  ) {
    const items = d.items;
    // Add length getter
    if (!("length" in d)) {
      Object.defineProperty(d, "length", { get() { return items.length; }, configurable: true });
    }
    // Make iterable
    if (!d[Symbol.iterator]) {
      d[Symbol.iterator] = function* () { for (const it of items) yield it; };
    }
    // Add Array methods that delegate to items
    ARRAY_METHODS.forEach(m => {
      if (typeof d[m] !== "function") {
        d[m] = (...args) => items[m](...args);
      }
    });
  }
  return response;
}, err => Promise.reject(err));

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) {
    return <SunLoader size="lg" text="Načítám..." fullscreen />;
  }
  if (isAuthenticated === false) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

function App() {
  // iter413bh — Apply user appearance preferences on app startup (before any
  // page renders). Reads from localStorage for instant restore; backend
  // hydration happens via useDensityToggle/Theme contexts on their own time.
  React.useEffect(() => {
    try {
      const density = localStorage.getItem("_global_table_density");
      if (density && ["ultra", "compact", "twoline", "comfortable"].includes(density)) {
        document.documentElement.setAttribute("data-density", density);
      }
    } catch {
      // localStorage may be unavailable in private/incognito; ignore.
    }
  }, []);
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRouter />
          <GlobalSmsDialog />
          <GlobalQuickCapture />
          <GlobalAutoNew />
          <StatusChangePromptDialog />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
